// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

// Dish Tech React
import {BreadCrumb, InputTextField} from '@dishtech-react/core';
import {useForm, useToast} from '@dishtech-react/hooks';
import {areRolesAuthorized} from '@dishtech-react/utils';

// Material UI
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import ReorderIcon from '@material-ui/icons/Reorder';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import {
  Button,
  Card,
  CardMedia,
  Checkbox,
  Chip,
  Fab,
  FormControlLabel,
  Grid,
  InputLabel,
  Paper,
  Typography,
} from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles} from '@material-ui/core/styles';

// Components
import {useSelector} from 'react-redux';
import AddAsset from './add-asset';
import AssetDataTable from './asset-data-table';
import MultiImagePicker from '../helpers/multi-image-picker';
import SortableComponent from './reorder-table';

// Configs
import {serviceConfig} from '../../../config/service-config';

// Hooks
import {useFolder} from '../../hooks/use-folder';

const orderButtonStyle = {
  borderRadius: '50%',
  color: 'rgba(0, 0, 0, 0.54)',
  flex: '0 0 auto',
  fontSize: '1.5rem',
  height: 48,
  marginTop: 24,
  minHeight: 48,
  minWidth: 48,
  overflow: 'visible',
  paddingBottom: 20,
  paddingLeft: 22,
  paddingRight: 10,
  paddingTop: 20,
  position: 'absolute',
  textAlign: 'center',
  transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  width: 48,
  zIndex: 1,
};

const useStyles = makeStyles(theme => ({
  border: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  breadcrumb: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  button: {
    margin: theme.spacing(2, 2, 1, 0),
  },
  muiTableButtonReorder: {
    left: 535,
    ...orderButtonStyle,
  },
  muiTableButtonDone: {
    left: 475,
    ...orderButtonStyle,
  },
  muiTableButtonCancel: {
    left: 535,
    ...orderButtonStyle,
  },
  card: {
    display: 'block',
    width: '300px',
    margin: '2em 0em',
  },
  chip: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  chipContainer: {
    boxShadow: 'none',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  deleteButton: {
    position: 'absolute',
    top: '32px',
    right: '32px',
  },
  grid: {
    flexWrap: 'nowrap',
  },
  gridItemLeft: {
    width: '600px',
  },
  gridItemRight: {
    width: '650px',
  },
  subFolders: {
    marginTop: theme.spacing(2),
  },
  subFoldersContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    boxShadow:
      '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
  },
  subFoldersLabel: {
    marginTop: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.87)',
  },
  subFoldersLabelReadOnly: {
    marginTop: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.87)',
    borderRadius: 25,
    background: '#e0e0e0',
    whiteSpace: 'pre-wrap',
    padding: '2px 10px 2px 10px',
    marginRight: 5,
    marginLeft: 5,
  },
  subFoldersLabelReadOnlyDiv: {
    display: 'inline-flex',
  },
  subfolderButton: {
    marginTop: theme.spacing(1),
  },
  text: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  updateButton: {
    position: 'absolute',
    top: '32px',
    right: '100px',
  },
  duplicate: {
    color: 'red',
    fontSize: 12,
  },
}));

const FORM_KEY = 'FOLDER';

const FolderForm = ({rippleRoles, onCancel, onSave}) => {
  const folderCreate = areRolesAuthorized(rippleRoles, 'FOLDER', ['CREATE']);
  const folderDelete = areRolesAuthorized(rippleRoles, 'FOLDER', ['DELETE']);
  const [generateNewLaunchId, setGenerateLaunchId] = useState(false);
  const [disableGenerateNewLaunchId, setDisableGenerateLaunchId] = useState(false);
  const [deleteLaunchId, setDeleteLaunchId] = useState(false);
  const [disableDeleteLaunchId, setDisableDeleteLaunchId] = useState(false);
  const classes = useStyles();
  const existingFoldersNames = useSelector(state =>
    state?.service?.FOLDER?.response?.featuredFolders?.length
      ? state?.service?.FOLDER?.response?.featuredFolders.map(f => f.name)
      : []
  );
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [isEmptyName, setIsEmptyName] = useState(false);
  const [originalName, setOriginalName] = useState('');

  const {
    addDataPackage,
    createFolder,
    deleteFolder,
    folderData,
    mode,
    removeDataPackage,
    resetFolder,
    updateFolder,
    viewFolder,
  } = useFolder();
  const {
    formValid,
    formValues,
    formValues: {
      dataPackages = [],
      description,
      folderID: folderId,
      name = '',
      launchId = '',
      parents = [],
      subFolders = [],
      posterImageURL,
      widescreenImageURL,
      widescreenBackgroundImageURL,
    },
    registerForm,
    resetForm,
    setFieldValue,
    submitForm,
  } = useForm(serviceConfig, FORM_KEY, mode);
  const {submitForm: submitUpdateForm} = useForm(serviceConfig, FORM_KEY, 'update');
  const {createToast} = useToast();
  const getAssets = () => {
    const assets = [];

    dataPackages.reduce((target, dataPackage) => {
      const {echostarId} = dataPackage.asset;

      if (!(echostarId in target)) {
        target[echostarId] = {...dataPackage.asset};
        target[echostarId].dataPackages = [];
        assets.push(target[echostarId]);
      }

      target[echostarId].dataPackages.push(dataPackage);

      return target;
    }, {});

    return assets;
  };

  const [reorderEnabled, switchReorder] = useState(false);
  const [reorderedAssetsTemporary, setReorderedAssetsTemporary] = useState([]);

  useEffect(() => {
    registerForm();

    setOriginalName(formValues?.name);
    return () => resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    const newFormValuesData = {...formValues};
    if (generateNewLaunchId) {
      newFormValuesData.generateNewLaunchId = generateNewLaunchId;
    } else if (deleteLaunchId) {
      newFormValuesData.launchId = null;
    }
    if (mode === 'create' && parents.length) {
      const newFormValues = {
        generateLaunchId: generateNewLaunchId,
        ...folderData,
        subFolders: [
          ...folderData.subFolders,
          {
            ...newFormValuesData,
            parents: undefined,
          },
        ],
      };

      submitUpdateForm(newFormValues).then(({error}) => {
        if (error) {
          createToast(error, {
            variant: 'error',
            persist: true,
            onClose: () => viewFolder(folderData),
          });
        } else {
          createToast('Successfully created sub-folder.');
          resetFolder();
          viewFolder(newFormValues);
        }
      });
    } else {
      submitForm(newFormValuesData).then(({error}) => {
        if (error) {
          createToast(error, {
            variant: 'error',
            persist: true,
            onClose: () => onSave(),
          });
        } else {
          createToast(`Successfully ${mode}d folder.`);
          onSave();
        }
      });
    }
  };

  const handleCancel = () => {
    if (mode === 'create' && parents.length) {
      viewFolder(folderData);
    } else {
      onCancel();
    }
  };

  const handleUpdateClick = () => {
    updateFolder(formValues);
  };

  const handleDeleteClick = () => {
    deleteFolder(formValues, false).catch(error =>
      createToast(error, {
        variant: 'error',
        persist: true,
      })
    );
  };

  const onDropHandler = (fieldName, filename) => {
    const reader = new FileReader();
    reader.readAsDataURL(filename);
    reader.onload = event => {
      const imageBytes = event.target.result.split(',')[1];
      setFieldValue(fieldName, imageBytes);
    };
    reader.onerror = event => {
      const errorCode = event.target.error.code;
      console.error(`Could not read the file. Encountered the following error code: ${errorCode}`);
    };
  };

  const onAdd = dataPackage => {
    addDataPackage(dataPackage);
  };

  const onRemove = dataPackageId => {
    removeDataPackage(dataPackageId);
  };

  const handleAddSubFolder = () => () => {
    createFolder(folderId);
  };

  const handleDeleteSubFolder = subFolder => () => {
    deleteFolder(subFolder);
  };

  const assets = getAssets();

  const writeReorderedAssets = reorderedAssetsIn => {
    setReorderedAssetsTemporary(reorderedAssetsIn);
  };

  const reorderButtons = () => (
    <>
      {!reorderEnabled ? (
        <Tooltip title="Reorder rows">
          <Button
            className={classes.muiTableButtonReorder}
            color="default"
            disabled={!assets.length || assets.length === 1}
            startIcon={<ReorderIcon />}
            variant="text"
            onClick={() => switchReorder(!reorderEnabled)}
          />
        </Tooltip>
      ) : (
        <>
          <Tooltip title="Save the modified order">
            <Button
              className={classes.muiTableButtonDone}
              color="default"
              disabled={!assets.length || assets.length === 1}
              startIcon={<DoneIcon />}
              variant="text"
              onClick={() => {
                if (reorderedAssetsTemporary.length) {
                  const tempFormValues = {...formValues};
                  tempFormValues.dataPackages = reorderedAssetsTemporary;
                  updateFolder(tempFormValues);
                }
                setReorderedAssetsTemporary([]);
                switchReorder(!reorderEnabled);
              }}
            />
          </Tooltip>
          <Tooltip title="Do not modify the order">
            <Button
              className={classes.muiTableButtonCancel}
              color="default"
              disabled={!assets.length || assets.length === 1}
              startIcon={<CloseIcon />}
              variant="text"
              onClick={() => {
                setReorderedAssetsTemporary([]);
                switchReorder(!reorderEnabled);
              }}
            />
          </Tooltip>
        </>
      )}
    </>
  );

  const setGenerateLaunchIdExtended = () => {
    setGenerateLaunchId(!generateNewLaunchId);
    if (generateNewLaunchId) {
      setDisableDeleteLaunchId(false);
    } else {
      setDisableDeleteLaunchId(true);
    }
  };

  const setDeleteLaunchIdExtended = () => {
    setDeleteLaunchId(!deleteLaunchId);
    if (deleteLaunchId) {
      setDisableGenerateLaunchId(false);
    } else {
      setDisableGenerateLaunchId(true);
    }
  };

  return (
    <>
      <Grid className={classes.grid} container={true} spacing={4}>
        <Grid className={classes.gridItemLeft} item={true}>
          {mode === 'view' ? (
            <>
              <Fab
                className={classes.updateButton}
                color="primary"
                disabled={!areRolesAuthorized(rippleRoles, 'FOLDER', ['UPDATE'])}
                size="medium"
                onClick={handleUpdateClick}
              >
                <EditIcon />
              </Fab>

              <Fab
                className={classes.deleteButton}
                color="secondary"
                disabled={!areRolesAuthorized(rippleRoles, 'FOLDER', ['DELETE'])}
                size="medium"
                onClick={handleDeleteClick}
              >
                <DeleteIcon />
              </Fab>
            </>
          ) : null}

          <BreadCrumb
            crumbRenderer={({name: crumbName}) => (
              <Typography className={classes.breadcrumb} key={crumbName}>
                {crumbName}
              </Typography>
            )}
            crumbs={[...parents, ...(mode !== 'create' ? [{name}] : [{name: name || '\u2002'}])]}
          />

          {mode === 'view' ? (
            <>
              <Typography className={classes.text} variant="body2">
                {description}
              </Typography>
              <div style={{flex: 'row'}}>
                {widescreenImageURL ? (
                  <div style={{width: '30em'}}>
                    <Card className={classes.card} key={widescreenImageURL}>
                      <CardMedia component="img" image={widescreenImageURL} />
                    </Card>
                  </div>
                ) : null}
                {widescreenBackgroundImageURL ? (
                  <div style={{width: '30em'}}>
                    <Card className={classes.card} key={widescreenBackgroundImageURL}>
                      <CardMedia component="img" image={widescreenBackgroundImageURL} />
                    </Card>
                  </div>
                ) : null}
                {posterImageURL ? (
                  <div style={{width: '30em'}}>
                    <Card className={classes.card} key={posterImageURL}>
                      <CardMedia component="img" image={posterImageURL} />
                    </Card>
                  </div>
                ) : null}
              </div>
              <Paper className={classes.subFoldersContainer}>
                <InputLabel className={classes.subFoldersLabel}>Sub-Folders</InputLabel>
                <Paper className={classes.chipContainer}>
                  {subFolders.map(subFolder =>
                    folderDelete ? (
                      <Chip
                        className={classes.chip}
                        key={subFolder.name}
                        label={subFolder.name}
                        onDelete={handleDeleteSubFolder(subFolder)}
                      />
                    ) : (
                      // eslint-disable-next-line react/jsx-key
                      <div className={classes.subFoldersLabelReadOnlyDiv}>
                        <span className={classes.subFoldersLabelReadOnly}>{subFolder.name}</span>
                      </div>
                    )
                  )}
                  {folderCreate && (
                    <Fab
                      className={classes.subfolderButton}
                      color="primary"
                      disabled={!name || !areRolesAuthorized(rippleRoles, 'FOLDER', ['UPDATE'])}
                      size="small"
                      onClick={handleAddSubFolder()}
                    >
                      <CreateNewFolderIcon />
                    </Fab>
                  )}
                </Paper>
              </Paper>
            </>
          ) : (
            <>
              <InputTextField
                autoFocus={true}
                className={classes.border}
                fullWidth={true}
                id="name"
                label="Name"
                margin="dense"
                required={mode === 'create'}
                type="text"
                value={name || ''}
                onChange={event => {
                  if (originalName) {
                    setIsDuplicateName(
                      existingFoldersNames.includes(event.target.value) &&
                        event.target.value.trim() !== originalName.trim()
                    );
                  } else {
                    setIsDuplicateName(existingFoldersNames.includes(event.target.value));
                  }
                  setIsEmptyName(!event.target.value || (event.target.value && event.target.value.trim().length === 0));
                  setFieldValue('name', event.target.value);
                }}
              />
              {isDuplicateName ? (
                <span className={classes.duplicate}>Another folder is already using that name</span>
              ) : null}
              {isEmptyName ? <span className={classes.duplicate}>The folder name cannot be blank</span> : null}
              <InputTextField
                autoFocus={false}
                className={classes.border}
                fullWidth={true}
                id="description"
                label="Description"
                margin="dense"
                multiline={true}
                type="text"
                value={description || ''}
                onChange={event => setFieldValue('description', event.target.value)}
              />
              <InputTextField
                autoFocus={true}
                className={classes.border}
                disabled={true}
                fullWidth={true}
                id="launchId"
                label="Launch ID"
                margin="dense"
                required={false}
                type="text"
                value={launchId || 'Launch ID is not assigned'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={generateNewLaunchId}
                    disabled={disableGenerateNewLaunchId}
                    name="gilad"
                    onChange={() => setGenerateLaunchIdExtended()}
                  />
                }
                label="Generate Launch ID on Submit"
              />
              {mode !== 'create' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={deleteLaunchId}
                      disabled={disableDeleteLaunchId}
                      name="gilad"
                      onChange={() => setDeleteLaunchIdExtended()}
                    />
                  }
                  label="Delete Launch ID on Submit"
                />
              )}
              <MultiImagePicker
                firstDisplayName="Poster 4:3"
                firstDisplayType="posterImage"
                firstImageRequire={true}
                firstImageURL={posterImageURL}
                secondDisplayName="Widescreen 16:9"
                secondDisplayType="widescreenImage"
                secondImageRequire={true}
                secondImageURL={widescreenImageURL}
                thirdDisplayName="Hero 16:9"
                thirdDisplayType="widescreenBackgroundImage"
                thirdImageRequire={false}
                thirdImageURL={widescreenBackgroundImageURL}
                onAdd={(fieldName, filename) => onDropHandler(fieldName, filename)}
                onDelete={fieldName => {
                  const validUrls = [
                    'posterImageBytes',
                    'posterImageURL',
                    'widescreenBackgroundImageBytes',
                    'widescreenBackgroundImageURL',
                    'widescreenImageBytes',
                  ];
                  if (validUrls.includes(fieldName)) {
                    setFieldValue(fieldName, '');
                  }
                }}
              />
            </>
          )}
          {mode !== 'view' && mode !== 'create' && reorderButtons()}

          {!reorderEnabled ? (
            <AssetDataTable
              canRemove={mode !== 'view'}
              data={assets}
              rippleRoles={rippleRoles}
              showSelectionControls={false}
              showToolbar={false}
              sort={false}
              onRemove={onRemove}
            />
          ) : (
            <SortableComponent data={formValues.dataPackages} writeReorderedAssets={writeReorderedAssets} />
          )}

          <Button
            className={classes.button}
            color="default"
            startIcon={<CancelIcon />}
            variant="contained"
            onClick={handleCancel}
          >
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>

          {mode !== 'view' ? (
            <Button
              className={classes.button}
              color="primary"
              disabled={!formValid || reorderEnabled || isDuplicateName || isEmptyName}
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
            >
              {mode}
            </Button>
          ) : null}
        </Grid>
        {mode !== 'view' ? (
          <Grid className={classes.gridItemRight} item={true}>
            <AddAsset reorderEnabled={reorderEnabled} rippleRoles={rippleRoles} sort={true} onAdd={onAdd} />
          </Grid>
        ) : null}
      </Grid>
    </>
  );
};

FolderForm.propTypes = {
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  rippleRoles: PropTypes.array,
};

export default FolderForm;
